/**
 * Templated Connect use cases, looked up by id. A use-case click sends only
 * the id to DANI (`/dani?usecase=<id>`); DANI finds the template here and
 * asks its questions (`questions.ts`), so the URL never carries a prompt and
 * a stale or hand-edited link can only name a known use case.
 *
 * A channel's templated use cases reach DANI only once the channel is listed
 * in `TEMPLATED_CHANNELS`.
 */

import { Routes } from '@/app/routes';

import { APP_CHANNEL } from './data/app';
import { ATTITUDES_CHANNEL } from './data/attitudes';
import { AUDIO_CHANNEL } from './data/audio';
import { DEMOGRAPHICS_CHANNEL } from './data/demographics';
import { DIGITAL_ADS_CHANNEL } from './data/digital-ads';
import { GEN_AI_CHATGPT_CHANNEL } from './data/gen-ai-chatgpt';
import { GEN_AI_GEMINI_CHANNEL } from './data/gen-ai-gemini';
import { LOCATION_CHANNEL } from './data/location';
import { MEDIA_CHANNEL } from './data/media';
import { PURCHASE_CHANNEL } from './data/purchase';
import { WEB_CHANNEL } from './data/web';
import type { SlotKey } from './prompt-template';
import type { ConnectChannel, ConnectChannelKey } from './types';

export const USE_CASE_PARAM = 'usecase';

export interface ConnectUseCase {
    id: string;
    name: string;
    icon: string;
    template: string;
    /** Finishes DANI's "I'll show you …" before the questions. */
    summary: string;
    /** The channel it belongs to: words the brand, competitor and survey
     * questions and picks which kind of competitor is recommended. */
    channel: ConnectChannelKey;
    /** The use case's own wording for some of its questions. */
    ask?: Partial<Record<SlotKey, string>>;
}

const TEMPLATED_CHANNELS: Array<{ channel: ConnectChannelKey; data: ConnectChannel }> = [
    { channel: 'demographics', data: DEMOGRAPHICS_CHANNEL },
    { channel: 'location', data: LOCATION_CHANNEL },
    { channel: 'app', data: APP_CHANNEL },
    { channel: 'web', data: WEB_CHANNEL },
    { channel: 'purchase', data: PURCHASE_CHANNEL },
    { channel: 'digital-ads', data: DIGITAL_ADS_CHANNEL },
    { channel: 'media', data: MEDIA_CHANNEL },
    { channel: 'audio', data: AUDIO_CHANNEL },
    { channel: 'gen-ai-chatgpt', data: GEN_AI_CHATGPT_CHANNEL },
    { channel: 'gen-ai-gemini', data: GEN_AI_GEMINI_CHANNEL },
    { channel: 'attitudes', data: ATTITUDES_CHANNEL },
];

export const CONNECT_USE_CASES: ConnectUseCase[] = TEMPLATED_CHANNELS.flatMap(({ channel, data }) =>
    data.actions.flatMap((action) =>
        action.template === undefined
            ? []
            : [
                  {
                      id: action.id,
                      name: action.name,
                      icon: action.icon,
                      template: action.template,
                      summary: action.summary,
                      channel,
                      ask: action.ask,
                  },
              ],
    ),
);

export function findConnectUseCase(id: string | null | undefined): ConnectUseCase | null {
    return CONNECT_USE_CASES.find((useCase) => useCase.id === id) ?? null;
}

export function connectUseCaseHref(id: string): string {
    return `${Routes.STUDIO_AI}?${USE_CASE_PARAM}=${encodeURIComponent(id)}`;
}
